import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    Reply as ReplyIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../hooks/useFetch';
import usePostData from '../../../../hooks/usePostData';
import useDeleteItem from '../../../../hooks/useDeleteItem';
import { formatJalaliDate, formatJalaliDateTime } from '../../../../utils/formatJalaliDate';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { API } from '../../../../utils/apiRoutes';
import api from '../../../../config/axiosConfig';
import { useAuth } from '../../../../context/AuthContext';
import ConfirmationDialog from '../ConfirmationDialog';
import FloatingSnackbar from '../../../common/FloatingSnackbar';

function ReplyItem({ reply, noteId, onReply, onEdit, onDelete, currentUser, depth = 0 }) {
    const theme = useTheme();
    const maxDepth = 3; // Limit nesting depth
    const canModify = reply.author === currentUser?.id || currentUser?.is_superuser || currentUser?.staff_role?.name === 'manager';
    const isEdited = reply.is_edited || (reply.updated_at && reply.created_at && new Date(reply.updated_at) > new Date(reply.created_at));
    
    return (
        <Box sx={{ 
            ml: depth > 0 ? 4 : 0, 
            mt: 1,
            borderLeft: depth > 0 ? `2px solid ${theme.palette.divider}` : 'none',
            pl: depth > 0 ? 2 : 0,
        }}>
            <Paper 
                elevation={1} 
                sx={{ 
                    p: 1.5, 
                    bgcolor: depth % 2 === 0 ? theme.palette.background.paper : theme.palette.background.neutral,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                                {reply.author_name || reply.author_email}
                            </Typography>
                            {isEdited && (
                                <Chip 
                                    label="ویرایش شده" 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ height: '20px', fontSize: '0.7rem' }}
                                />
                            )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {formatJalaliDateTime(reply.created_at) && toPersianDigits(formatJalaliDateTime(reply.created_at))}
                            {isEdited && ` (ویرایش: ${formatJalaliDateTime(reply.updated_at) && toPersianDigits(formatJalaliDateTime(reply.updated_at))})`}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {depth < maxDepth && (
                            <IconButton
                                size="small"
                                onClick={() => onReply(reply.id)}
                                title="پاسخ"
                                sx={{ fontSize: '0.875rem' }}
                            >
                                <ReplyIcon fontSize="small" />
                            </IconButton>
                        )}
                        {canModify && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={() => onEdit(reply)}
                                    title="ویرایش"
                                    color="primary"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => onDelete(reply)}
                                    title="حذف"
                                    color="error"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {reply.content}
                </Typography>
                
                {/* Render child replies recursively */}
                {reply.child_replies && reply.child_replies.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        {reply.child_replies.map((childReply) => (
                            <ReplyItem
                                key={childReply.id}
                                reply={childReply}
                                noteId={noteId}
                                onReply={onReply}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                currentUser={currentUser}
                                depth={depth + 1}
                            />
                        ))}
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

function ReplyThread({ noteId, onClose }) {
    const theme = useTheme();
    const { user: currentUser } = useAuth();
    const [replyText, setReplyText] = useState('');
    const [parentReplyId, setParentReplyId] = useState(null);
    const [editingReply, setEditingReply] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [replyToDelete, setReplyToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editText, setEditText] = useState('');
    
    const { deleteItem, isLoading: isDeleting } = useDeleteItem();
    const [postData, isCreating] = usePostData(API.colleagueNoteReplies);
    const [isUpdating, setIsUpdating] = useState(false);
    
    // Fetch replies with polling (every 45 seconds)
    const { data: repliesData, loading, error, refetch } = useFetch(API.colleagueNoteReplies, {
        params: { note_id: noteId },
        skip: !noteId,
    });
    
    // Polling effect - refetch every 45 seconds
    useEffect(() => {
        if (!noteId) return;
        
        const interval = setInterval(() => {
            refetch();
        }, 45000); // 45 seconds
        
        return () => clearInterval(interval);
    }, [noteId, refetch]);
    
    // Process replies to build nested structure (only top-level replies)
    const topLevelReplies = useMemo(() => {
        if (!repliesData || !Array.isArray(repliesData)) return [];
        // Filter only top-level replies (parent_reply is null)
        return repliesData.filter(reply => !reply.parent_reply);
    }, [repliesData]);
    
    const handleReply = useCallback((parentId = null) => {
        setParentReplyId(parentId);
        setReplyText('');
    }, []);
    
    const handleSubmitReply = useCallback(async () => {
        if (!replyText.trim()) return;
        
        try {
            const payload = {
                note: noteId,
                content: replyText.trim(),
                ...(parentReplyId && { parent_reply: parentReplyId }),
            };
            
            const result = await postData(payload);
            if (result && result.success) {
                setSnackbar({
                    open: true,
                    message: 'پاسخ با موفقیت ارسال شد.',
                    severity: 'success',
                });
                setReplyText('');
                setParentReplyId(null);
                refetch();
            } else {
                throw new Error(result?.error || 'خطا در ارسال پاسخ');
            }
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || err?.message || 'خطا در ارسال پاسخ',
                severity: 'error',
            });
        }
    }, [replyText, noteId, parentReplyId, postData, refetch]);
    
    const handleEdit = useCallback((reply) => {
        setEditingReply(reply);
        setEditText(reply.content);
        setEditDialogOpen(true);
    }, []);
    
    const handleUpdateReply = useCallback(async () => {
        if (!editText.trim() || !editingReply) return;
        
        try {
            setIsUpdating(true);
            await api.patch(`${API.colleagueNoteReplies}${editingReply.id}/`, {
                content: editText.trim(),
            });
            setSnackbar({
                open: true,
                message: 'پاسخ با موفقیت به‌روزرسانی شد.',
                severity: 'success',
            });
            setEditDialogOpen(false);
            setEditingReply(null);
            setEditText('');
            refetch();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در به‌روزرسانی پاسخ',
                severity: 'error',
            });
        } finally {
            setIsUpdating(false);
        }
    }, [editText, editingReply, refetch]);
    
    const handleDelete = useCallback((reply) => {
        setReplyToDelete(reply);
        setDeleteDialogOpen(true);
    }, []);
    
    const confirmDelete = useCallback(async () => {
        if (!replyToDelete) return;
        try {
            await deleteItem(`${API.colleagueNoteReplies}${replyToDelete.id}/`);
            setSnackbar({
                open: true,
                message: 'پاسخ با موفقیت حذف شد.',
                severity: 'success',
            });
            refetch();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در حذف پاسخ',
                severity: 'error',
            });
        } finally {
            setDeleteDialogOpen(false);
            setReplyToDelete(null);
        }
    }, [replyToDelete, deleteItem, refetch]);
    
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">پاسخ‌ها</Typography>
                <Button onClick={onClose} size="small">بستن</Button>
            </Box>
            
            {/* Reply input */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={parentReplyId ? "پاسخ خود را بنویسید..." : "پاسخ جدید..."}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    variant="outlined"
                    size="small"
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    {parentReplyId && (
                        <Button
                            size="small"
                            onClick={() => {
                                setParentReplyId(null);
                                setReplyText('');
                            }}
                        >
                            انصراف
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={handleSubmitReply}
                        disabled={!replyText.trim() || isCreating}
                        size="small"
                    >
                        {isCreating ? 'در حال ارسال...' : 'ارسال پاسخ'}
                    </Button>
                </Box>
            </Paper>
            
            {/* Replies list */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography align="center" color="error" sx={{ p: 2 }}>
                    {error?.message || 'خطا در بارگذاری پاسخ‌ها'}
                </Typography>
            ) : topLevelReplies.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    هنوز پاسخی ارسال نشده است.
                </Typography>
            ) : (
                <Box>
                    {topLevelReplies.map((reply) => (
                        <ReplyItem
                            key={reply.id}
                            reply={reply}
                            noteId={noteId}
                            onReply={handleReply}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            currentUser={currentUser}
                        />
                    ))}
                </Box>
            )}
            
            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>ویرایش پاسخ</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>انصراف</Button>
                    <Button
                        onClick={handleUpdateReply}
                        variant="contained"
                        disabled={!editText.trim() || isUpdating}
                    >
                        {isUpdating ? 'در حال ذخیره...' : 'ذخیره'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Delete Confirmation */}
            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setReplyToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="تأیید حذف"
                message="آیا مطمئن هستید که می‌خواهید این پاسخ را حذف کنید؟"
                confirmColor="error"
                isLoading={isDeleting}
            />
            
            {/* Snackbar */}
            <FloatingSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </Box>
    );
}

export default ReplyThread;

